// FRONT-1717: which background-chat runs already have a LOCAL writer for
// their post-stream `/history` refresh.
//
// At turn end there were two independent writers to `chatKeys.history` for
// the SAME turn, each producing a fresh `history` reference and each waking
// `use-agent-chat.ts`'s reconcile effect:
//
//  1. `use-agent-chat.ts`'s `onFinish` — fetches `/history` and writes it
//     with `queryClient.setQueryData`. It needs that payload locally anyway
//     (`resumeTargetToSuppress`), it is tied to the stream completion we
//     actually trust rather than to an SSE round trip, and FRONT-1685
//     deliberately kept it ("the cache write and the invalidations still
//     belong here") while deleting a THIRD writer, the redundant in-hook
//     `chat.setMessages`. So this is the writer that survives.
//  2. `use-sse.ts`'s `background-chat-changed` handler — invalidates the
//     same key, which is a real refetch under `staleTime: 0`.
//
// Writer 2 cannot simply be deleted: a tab that did NOT run the turn (a web
// chat closed and reopened mid-run — FRONT-542) has no local stream, so no
// `onFinish` ever fires there and the SSE invalidation is the only thing
// that paints the answer.
//
// The discriminator is therefore "did THIS tab run the run this settle is
// announcing", and this registry answers it by run identity:
//
//  * `src/agents/chat-proxy.ts` mints one `bgChatRunId` per tracked turn
//    (`:3628`, gated on `shouldTrackBackgroundChat`), streams it to the
//    client that asked for the turn as a transient chunk (`:3720-3721`),
//    and settles that same id in the request's `finally` (`:6035-6063`) —
//    all three under the one `bgChatRunId` variable. So the chunk and the
//    settle event are co-gated, and the chunk provably arrives FIRST: it is
//    emitted inside the stream the settle only happens after. The ordering
//    is causal, not a timing race, which is what lets this be a claim
//    consumed later rather than a "did the other writer already run" poll.
//  * A tab that never streamed the turn never receives the chunk, so it
//    holds no claim and its settle invalidates exactly as before.
//
// Keyed by runId rather than by session on purpose: a session-keyed claim
// would suppress a settle belonging to a DIFFERENT run on the same session
// (a background run overlapping a local turn). Keyed by run there is no
// wrong-suppression case at all — a claim can only ever be consumed by the
// settle of the very run that created it.
//
// Module scope (mirrors `resume-retry-policy.ts`'s registry) so it survives
// a component remount, and per-tab because each tab loads its own module
// instance — which is exactly the granularity wanted here.

/**
 * Cap on tracked runs. A claim normally lives from turn start to that
 * turn's settle (seconds), and is consumed there. The cap is what bounds
 * the ones that are never consumed — a turn whose settle never reaches this
 * tab (SSE dropped, tab offline at settle time). Oldest-first eviction; 50
 * is far more turns than a tab has in flight at once.
 */
export const MAX_TRACKED_RUNS = 50;

// Insertion-ordered by construction (`Set` iterates in insertion order), so
// the first entry is always the oldest claim.
const claimed = new Set<string>();

/**
 * Claim a run's post-stream history write for this tab's local `onFinish`.
 * Called from `use-agent-chat.ts`'s `onData` the moment the turn's runId
 * chunk arrives — at turn start, before any settle for it can exist; or, on
 * a re-attach, when the replayed chunk arrives (it is on the tape), which is
 * why `handOffAfterStreamEnd` gives the claim back when that stream ends
 * before the turn does.
 */
export function claimLocalHistoryWrite(runId: string): void {
  if (!runId) return;
  // Re-claiming refreshes recency so a long-lived run can't be evicted by
  // newer, shorter ones while it is still streaming.
  claimed.delete(runId);
  claimed.add(runId);
  while (claimed.size > MAX_TRACKED_RUNS) {
    const oldest: string | undefined = claimed.values().next().value;
    if (oldest === undefined) break;
    claimed.delete(oldest);
  }
}

/**
 * Does a local writer own this run's settle? Consumes the claim, so a
 * LATER settle on the same session with no local turn behind it (the
 * FRONT-542 reopen path) invalidates normally.
 *
 * Returns false for a missing/absent runId — the fail-open direction. The
 * cost of a redundant invalidation is one wasted `/history` round trip; the
 * cost of a wrongly suppressed one is an answer that never paints.
 */
export function consumeLocalHistoryWrite(runId: string | null | undefined): boolean {
  if (!runId) return false;
  return claimed.delete(runId);
}

/**
 * Give the claim back without consuming a settle, so the SSE handler goes
 * back to being the writer for this run.
 *
 * Two callers:
 *  - a SESSION CHANGE in `use-agent-chat` (directly). The hook instance can
 *    outlive one session (`pwa-shell.tsx` switches sessions on the same
 *    mounted instance without aborting the previous stream), and the AI SDK
 *    routes `onFinish` through a ref it refreshes every render — so the
 *    orphaned stream's `onFinish` closes over the NEW session id and writes
 *    that key, never the outgoing session's. The outgoing run must therefore
 *    stop claiming a local write that will not land where it promised.
 *  - `handOffAfterStreamEnd` below, when a stream ended WITHOUT delivering
 *    the turn's final state (FRONT-2195).
 *
 * Never called from `onError` itself. `onFinish` is called from a `finally`
 * in the SDK's `Chat.makeRequest` (`ai@6.0.174`), so it runs after `onError`
 * as well — and it is `onFinish`'s own fetched `/history` that decides, via
 * `handOffAfterStreamEnd`, whether this stream's write was the final one.
 */
export function releaseLocalHistoryWrite(runId: string | null | undefined): void {
  if (!runId) return;
  claimed.delete(runId);
}

/** A structural `/history` row — only what `handOffAfterStreamEnd` reads. */
interface HistoryRowShape {
  role: string;
  metadata?: unknown;
}

/**
 * True when the trailing row is a FINISHED assistant row: the server drops
 * `streamStatus` once a turn is final (`src/agents/stream-lifecycle-meta.ts`
 * sets it only for a row still at status "streaming").
 */
function trailingRowIsFinal(history: readonly HistoryRowShape[]): boolean {
  const last = history[history.length - 1];
  if (!last || last.role !== 'assistant') return false;
  const meta = last.metadata;
  if (!meta || typeof meta !== 'object') return true;
  return (meta as { streamStatus?: unknown }).streamStatus == null;
}

/**
 * FRONT-2195 review F1: what `onFinish` does with this run's claim once it
 * has fetched `/history` (`history`, or null when that fetch failed).
 *
 * The claim promises "my `onFinish` writes the final answer". That holds only
 * when the stream that just ended actually carried the turn to its end. A
 * re-attach stream (every replay re-claims — the runId chunk is on the tape)
 * can end while the turn is still running: a resume-endpoint ceiling, a
 * dropped connection, a Stop whose server-side cancel lands a round later.
 * Its `onFinish` then writes a still-running snapshot, and a claim left in
 * place makes the SSE settle for this run defer to that write — so the final
 * answer never painted until the chat was reopened.
 *
 *  - `'kept'`: the fetched trailing row is final — this write IS the answer,
 *    and the settle must stay silent (FRONT-1717's one-writer rule).
 *  - `'released'`: the turn is not final (or the fetch failed, so nobody
 *    knows) — the claim is given back and the settle will refetch.
 *  - `'settled-meanwhile'`: same, but the claim was already gone, most
 *    likely because the settle consumed it while the fetch was in flight,
 *    so the snapshot just fetched predates it. The caller refetches once.
 *    (An evicted or never-held claim lands here too; one extra refetch is
 *    the fail-open cost.)
 *  - `'none'`: this stream never learned its runId, so it holds no claim.
 */
export function handOffAfterStreamEnd(
  runId: string | null | undefined,
  history: readonly HistoryRowShape[] | null,
): 'kept' | 'released' | 'settled-meanwhile' | 'none' {
  if (!runId) return 'none';
  if (history && trailingRowIsFinal(history)) return 'kept';
  return claimed.delete(runId) ? 'released' : 'settled-meanwhile';
}

/** Test-only: clear all claims between cases. */
export function __resetLocalHistoryWritersForTests(): void {
  claimed.clear();
}
